import categories from '@data/cats';
import countries from '@data/countries';
import extraCategories from '@data/extra-categories.json';
import { mergeCategories } from '@data/merge-categories';
import numeral from 'numeral';
import slugify from 'slugify';
import config from '@/config';

const localAddresses = ['::1', '127.0.0.1', 'localhost', '::ffff:127.0.0.1'];

const baseUrl = config.commerceApiUrl;

const CLIENT_NAME = 'giftcards-web-netlify';
const SERVICE_KEY_HEADER = 'x-bidali-service-key';

// Server-only (no NEXT_PUBLIC_): set on Netlify/build hosts so SSR and API
// routes identify as a trusted internal caller. Browser bundles never see them.
const giftcardsApiServiceKey = process.env.GIFTCARDS_API_SERVICE_KEY;
const paymentsApiServiceKey = process.env.PAYMENTS_API_SERVICE_KEY;

/** Identify this app on outbound fetches; Node's default user agent is `node`. */
const fetchWithServiceKey = (serviceKey) => (input, init = {}) => {
  const headers = new Headers(init.headers);
  headers.set('x-bidali-client', CLIENT_NAME);
  headers.set('user-agent', CLIENT_NAME);
  if (serviceKey) {
    headers.set(SERVICE_KEY_HEADER, serviceKey);
  }
  return fetch(input, { ...init, headers });
};

/** Commerce API fetches (giftcards-api). */
export const fetchApi = fetchWithServiceKey(giftcardsApiServiceKey);

/** Payments API fetches from getStaticProps / server code. */
export const fetchPaymentsApi = fetchWithServiceKey(paymentsApiServiceKey);

/*
 * cats.json already holds the hidden categories we sell (Health & Spa):
 * format-data.js appends data/extra-categories.json. Merging again costs
 * nothing, adds no duplicate, and covers a cats.json written before it did.
 */
const allCategories = mergeCategories(categories, extraCategories);
const categoryById = new Map(allCategories.map((category) => [category.id, category]));

export const countryfetcher = (...args) =>
  fetchApi(...args)
    .then((res) => res.json())
    .then((result) => result[0]);

/**
 * Country for an IP via the commerce API. Resolves to `fallback` (default
 * 'US') for local addresses and failed lookups; pass `{ fallback: null }`
 * when an unknown location must stay unknown (see pages/api/geo.js).
 */
export const lookupCountry = async (ip, { fallback = 'US' } = {}) => {
  const params = {};
  let countryId = fallback;

  console.log('Default country:', countryId);
  console.log('API URL:', baseUrl);

  try {
    if (!localAddresses.includes(ip)) {
      console.log('Looking up country for IP:', ip);
      params.query = { ip };
      const response = await fetchApi(`${baseUrl}/country-lookup?ip=${ip}`);
      const result = await response.json();
      console.log('Got a country from IP:');
      console.log(result);

      if (result.countryId) {
        countryId = result.countryId;
      }
    }
  } catch (error) {
    console.log('Error getting country from IP', error);
  }

  return countryId;
};

export const fetchBrandsInCategoryAndCountry = async ({
  countryId,
  categoryId,
  $search,
  $limit,
}) => {
  // const query = {
  //   countryId: countryId,
  //   'categories[0]': categoryId,
  //   enabled: true,
  //   discontinued: false,
  //   thirdPartyDisabled: false,
  //   $limit: 12,
  //   paginate: false
  // };
  // const response = await app.service('brands').find({ query });
  // return response.data;
  const queryParams = {
    countryId: countryId,
    enabled: true,
    discontinued: false,
    thirdPartyDisabled: false,
    paginate: false,
  };

  let url = `${baseUrl}/brands?${new URLSearchParams(queryParams)}`;

  if (categoryId) {
    url = `${url}&categories[0]=${categoryId}`;
  }

  if ($search) {
    url = `${url}&$search=${encodeURIComponent($search)}`;
  }

  if ($limit) {
    url = `${url}&$limit=${encodeURIComponent($limit)}`;
  }

  console.log(queryParams, url);
  const res = await fetchApi(url);
  const result = await res.json();
  return result.filter((brand) => brand.products.length > 0 && brand.products[0].currencyEnabled);
};

export const searchBrands = async ({ countryId, $search }) => {
  const queryParams = { minimal: true };
  const otherParams = { minimal: true };

  if (countryId) {
    queryParams.countryId = countryId;
    otherParams['countryId[$nin][]'] = countryId;
  }

  if ($search) {
    queryParams.$search = $search;
    otherParams.$search = $search;
  }

  const url = `${baseUrl}/search?${new URLSearchParams(queryParams)}`;
  const res = await fetchApi(url);
  const results = await res.json();

  if ($search && countryId) {
    const otherUrl = `${baseUrl}/search?${new URLSearchParams(otherParams)}`;
    const otherRes = await fetchApi(otherUrl);
    const otherResults = await otherRes.json();

    return { results, otherResults };
  }

  return { results };
};

export const countryForSlug = async (slug) => {
  let country = countries.find((c) => c.slug === slug);

  if (!country) {
    console.log('country not found, fetching');
    country = await countryfetcher(`${baseUrl}/countries?slug=${slug}`);
    console.log(country);
  }

  return country;
};

export const countryForId = async (id) => {
  let country = countries.find((c) => c.id === id);

  if (!country) {
    console.log('country not found, fetching');
    country = await countryfetcher(`${baseUrl}/countries/${id}`);
    console.log(country);
  }

  return country;
};

export const categoryForSlug = async (slug) => {
  console.log(`categoryForSlug`, slug);
  return allCategories.find((c) => c.slug === slug);
};

/** Prefer the commerce API slug; never derive from display names ( "/" → wrong slugs). */
const slugForCategory = (category) => {
  const known = categoryById.get(category.id);
  if (known?.slug) return known.slug;
  if (category.slug) return category.slug;
  return slugify(category.name, { lower: true });
};

export const fetchBrand = async ([countrySlug, slug, apiKey]) => {
  let country;

  try {
    country = await countryForSlug(countrySlug);

    if (!country) {
      console.log('COUNTRY DOES NOT EXIST', countrySlug);
      return null;
    }

    const queryParams = {
      countryId: country.id,
      enabled: true,
      // discontinued: false,
      thirdPartyDisabled: false,
      slug,
      paginate: false,
    };
    if (apiKey) {
      queryParams.apiKey = apiKey;
    }
    const url = `${baseUrl}/brands?${new URLSearchParams(queryParams)}`;
    const res = await fetchApi(url);
    const brandsResult = await res.json();
    const filteredBrands = brandsResult.filter(
      (b) => b.products.length > 0 && b.products[0].currencyEnabled
    );
    const brand = filteredBrands.find((b) => !b.discontinued) || filteredBrands[0];

    if (brand) {
      brand.isOutOfStock = brand.discontinued;
      brand.products.forEach((p) => {
        if (p.minPriceInCents) {
          p.minPriceInCents = numeral(p.minPriceInCents).value();
        }
        if (p.maxPriceInCents) {
          p.maxPriceInCents = numeral(p.maxPriceInCents).value();
        }
      });
      brand.categories = brand.categories.map((c) => {
        return {
          id: c.id,
          name: c.name,
          slug: slugForCategory(c),
        };
      });
    }

    return brand;
  } catch (e) {
    console.log('Error fetching brand', countrySlug, slug, country);
    console.log(e);
    e.data = { countrySlug, slug };
    throw e;
  }
};
